import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getAnalyticsCharts } from '../../services/api';
import './DashboardCommon.css';
import smLogo from '../../assets/SM_logo-removebg.png';
import ClientList from '../ClientList';
import BrandList from '../BrandList';
import ParticipatingTenderList from '../ParticipatingTenderList';
import MyTechnicalBidTasks from '../MyTechnicalBidTasks';
import MyAssignedTasks from '../MyAssignedTasks';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../NotificationBell';

function SMProductManagerDashboard({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [dashboardData, setDashboardData] = useState({ stats: null, charts: null });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  useEffect(() => {
    if (activeMenu !== 'dashboard') return;
    let cancelled = false;
    setDashboardLoading(true);
    setDashboardError(null);
    getAnalyticsCharts()
      .then((res) => {
        if (!cancelled && res.success) {
          setDashboardData({ stats: res.stats || {}, charts: res.charts || {} });
        }
      })
      .catch((err) => { if (!cancelled) setDashboardError(err.message); })
      .finally(() => { if (!cancelled) setDashboardLoading(false); });
    return () => { cancelled = true; };
  }, [activeMenu]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'home' },
    { id: 'technical-bids', label: 'My Technical Bid Tasks', icon: 'file' },
    { id: 'myTasks', label: 'My Tasks', icon: 'checkSquare' },
    { id: 'participating', label: 'Participating Tenders', icon: 'check' },
    { id: 'brands', label: 'Brands & Products', icon: 'package' },
    { id: 'clients', label: 'Client List', icon: 'building' },
  ];

  const getPageInfo = () => {
    if (activeMenu === 'dashboard') return { breadcrumb: [], title: 'Dashboard' };
    const item = menuItems.find(m => m.id === activeMenu);
    return item ? { breadcrumb: [item.label], title: item.label } : { breadcrumb: [], title: '' };
  };

  const pageInfo = getPageInfo();

  const statsFromApi = dashboardData.stats;
  const stats = statsFromApi
    ? [
        { label: 'My Technical Bid Tasks', value: String(statsFromApi.my_technical_bid_tasks ?? '–'), change: 'Assigned', color: '#10b981' },
        { label: 'Participating Tenders', value: String(statsFromApi.participating ?? '–'), change: 'Active', color: '#3b82f6' },
        { label: 'Brands', value: '–', change: 'Manage', color: '#f59e0b' },
        { label: 'Clients', value: '–', change: 'View', color: '#6b7280' },
      ]
    : [
        { label: 'My Technical Bid Tasks', value: '–', change: 'Assigned', color: '#10b981' },
        { label: 'Participating Tenders', value: '–', change: 'Active', color: '#3b82f6' },
        { label: 'Brands', value: '–', change: 'Manage', color: '#f59e0b' },
        { label: 'Clients', value: '–', change: 'View', color: '#6b7280' },
      ];

  const renderIcon = (name) => {
    const icons = {
      home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>,
      check: <><polyline points="20 6 9 17 4 12"></polyline></>,
      building: <><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></>,
      file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></>,
      package: <><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></>,
      menu: <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>,
      bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></>,
      checkSquare: <><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></>,
    };
    return icons[name] || null;
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'technical-bids':
        return <MyTechnicalBidTasks user={user} onGoToParticipating={() => setActiveMenu('participating')} />;
      case 'myTasks':
        return <MyAssignedTasks user={user} />;
      case 'participating':
        return <ParticipatingTenderList user={user} canManage={false} />;
      case 'brands':
        return <BrandList user={user} canEdit={true} />;
      case 'clients':
        return <ClientList user={user} canEdit={false} />;
      case 'dashboard':
      default:
        return (
          <>
            <div className="content-header">
              <h2>Welcome, {user?.full_name || user?.username || 'Product Manager'}!</h2>
              <p>Manage brands & products and submit technical bids for tenders.</p>
            </div>
            {dashboardError && (
              <div className="error-message" style={{ marginBottom: 16 }}>
                {dashboardError}
                <button type="button" onClick={() => setDashboardError(null)}>×</button>
              </div>
            )}
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-content">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-value">{dashboardLoading ? '…' : stat.value}</span>
                    <span className="stat-change" style={{ color: stat.color }}>{stat.change}</span>
                  </div>
                </div>
              ))}
            </div>
            {!dashboardLoading && dashboardData.charts && dashboardData.charts.my_technical_bids_by_status && dashboardData.charts.my_technical_bids_by_status.length > 0 && (
              <div className="chart-card" style={{ marginBottom: 24 }}>
                <h3>My Technical Bid Tasks by Status</h3>
                <div style={{ height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dashboardData.charts.my_technical_bids_by_status}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#f59e0b" name="Count" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="placeholder-section">
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('technical-bids')}>
                <h3>My Technical Bid Tasks</h3>
                <p>View and complete technical bid assignments.</p>
                <button className="placeholder-btn active">My Tasks</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('participating')}>
                <h3>Participating Tenders</h3>
                <p>View tenders and submit technical bids.</p>
                <button className="placeholder-btn active">View Tenders</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('brands')}>
                <h3>Brands & Products</h3>
                <p>Manage product brands and catalogs.</p>
                <button className="placeholder-btn active">Manage Brands</button>
              </div>
              <div className="placeholder-card clickable" onClick={() => setActiveMenu('clients')}>
                <h3>Client List</h3>
                <p>View company clients.</p>
                <button className="placeholder-btn active">View Clients</button>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-tooltip={sidebarOpen ? 'Collapse' : 'Expand'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sidebarOpen ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
          </svg>
        </button>
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => setActiveMenu(item.id)}
              data-tooltip={item.label}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon(item.icon)}</svg>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <header className="topbar">
        <div className="topbar-left">
          <img src={smLogo} alt="SM Systems" className="topbar-logo" />
          <div className="topbar-divider"></div>
          <h1 className="page-title">SM Product Manager</h1>
        </div>
        <div className="topbar-right">
          <NotificationBell />
          <ThemeToggle />
          <div className="user-profile-wrapper">
            <button className="user-profile" onClick={() => setProfileDropdown(!profileDropdown)}>
              <div className="avatar">{(user?.full_name || user?.username)?.charAt(0) || 'P'}</div>
              <div className="user-info">
                <span className="user-name">{user?.full_name || user?.username || 'Product Manager'}</span>
                <span className="user-role">SM Product Manager</span>
              </div>
              <svg className={`dropdown-arrow ${profileDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {profileDropdown && (
              <div className="profile-dropdown">
                <button className="dropdown-item" onClick={onLogout}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{renderIcon('logout')}</svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <div className="content">
          <div className="content-title-area">
            {pageInfo.breadcrumb.length > 0 && (
              <nav className="breadcrumb" aria-label="Breadcrumb">
                {pageInfo.breadcrumb.map((label, i) => (
                  <span key={i}>
                    {i > 0 && <span className="breadcrumb-sep"> / </span>}
                    <span>{label}</span>
                  </span>
                ))}
              </nav>
            )}
            <h2 className="content-page-title">{pageInfo.title}</h2>
          </div>
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default SMProductManagerDashboard;
